import React, { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  keyframes,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useOptimization } from '../../context/OptimizationContext';
import StatisticsView from '../StatisticsView/StatisticsView';
import { calculateCombinedWinner } from '../../utils/combinedWinnerUtils';

// Define keyframe animations
const flashBest = keyframes`
  0%, 100% {
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    background-color: rgba(76, 175, 80, 0.3);
    box-shadow: 0 0 8px 4px rgba(76, 175, 80, 0.6);
  }
`;

const flashWorst = keyframes`
  0%, 100% {
    background-color: rgba(211, 47, 47, 0.1);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4);
  }
  50% {
    background-color: rgba(211, 47, 47, 0.3);
    box-shadow: 0 0 8px 4px rgba(211, 47, 47, 0.6);
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ResultsView: React.FC = () => {
  const { results } = useOptimization();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const algorithms = useMemo(() => ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'], []);
  const algorithmKeys = useMemo(() => ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const, []);

  const hasResults = results !== null;

  // Generate analysis data for rendering
  const analysisData = useMemo(() => {
    if (!results) return null;

    // Fitness comparison - filter out undefined results
    const fitnessData = algorithmKeys
      .filter(key => results[key] !== undefined)
      .map(key => ({
        name: algorithms[algorithmKeys.indexOf(key)],
        fitness: results[key]!.fitness,
        key,
      }))
      .sort((a, b) => b.fitness - a.fitness);

    // Energy comparison - filter out undefined results
    const energyData = algorithmKeys
      .filter(key => results[key] !== undefined)
      .map(key => ({
        name: algorithms[algorithmKeys.indexOf(key)],
        energy: results[key]!.energy,
        key,
      }))
      .sort((a, b) => a.energy - b.energy);

    // Response Time comparison - filter out undefined results
    const responseTimeData = algorithmKeys
      .filter(key => results[key] !== undefined)
      .map(key => ({
        name: algorithms[algorithmKeys.indexOf(key)],
        responseTime: results[key]!.responseTime,
        key,
    })).sort((a, b) => a.responseTime - b.responseTime);

    return {
      fitnessData,
      energyData,
      responseTimeData,
    };
  }, [results, algorithms, algorithmKeys]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Results
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={value} 
          onChange={handleChange} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 80, sm: 100 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px 12px', sm: '12px 16px' },
            }
          }}
        >
          <Tab label="Solutions" />
          <Tab label="Fitness" />
          <Tab label="Config" />
          <Tab label="Energy" />
          <Tab label="Cost" />
          <Tab label="RT" />
          <Tab label="Analysis" />
          <Tab label="Combined Winner" />
          {results && 'statistics' in results && results.statistics && (
            <Tab label="Stats" />
          )}
        </Tabs>
      </Box>

      {/* Solutions Tab */}
      <TabPanel value={value} index={0}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            maxHeight: { xs: 300, md: 400 },
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              maxWidth: { xs: 120, md: 200 },
              wordBreak: 'break-all',
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
              padding: { xs: '8px 4px', sm: '12px 8px', md: '16px' },
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell 
                    key={alg} 
                    align="center" 
                    sx={{ 
                      fontWeight: 600, 
                      backgroundColor: 'background.paper',
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                    }}
                  >
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (
                <TableRow>
                  {algorithmKeys.map((key) => {
                    const result = results![key];
                    if (!result) {
                      return (
                        <TableCell 
                          key={key} 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                          }}
                        >
                          -
                        </TableCell>
                      );
                    }
                    const solutionStr = result.solution.join(', ');
                    const truncated = solutionStr.length > 50 ? solutionStr.substring(0, 50) + '...' : solutionStr;
                    
                    return (
                      <TableCell 
                        key={key} 
                        align="center"
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                        }}
                        title={solutionStr}
                      >
                        {truncated}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {hasResults && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            💡 Hover over solution values to see full content. Solutions show selected service indices for each task.
          </Typography>
        )}
      </TabPanel>

      {/* Fitness Tab */}
      <TabPanel value={value} index={1}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center" sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max fitness values - filter out undefined
                const fitnessValues = algorithmKeys
                  .filter(key => results![key] !== undefined)
                  .map(key => results![key]!.fitness);
                if (fitnessValues.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No results available yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                const minFitness = Math.min(...fitnessValues);
                const maxFitness = Math.max(...fitnessValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const result = results![key];
                      if (!result) {
                        return (
                          <TableCell key={key} align="center">-</TableCell>
                        );
                      }
                      const fitness = result.fitness;
                      const isBest = fitness === maxFitness;
                      const isWorst = fitness === minFitness && maxFitness !== minFitness;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {fitness.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Configuration Tab */}
      <TabPanel value={value} index={2}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ overflowX: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Initial Population</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Crossover</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Mutation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (
                <TableRow>
                  <TableCell>{results!.configuration.taskCount}</TableCell>
                  <TableCell>{results!.configuration.propertyCount}</TableCell>
                  <TableCell>{results!.configuration.initialPopulation}</TableCell>
                  <TableCell>{results!.configuration.crossover}%</TableCell>
                  <TableCell>{results!.configuration.mutation}%</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Energy Tab */}
      <TabPanel value={value} index={3}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center">
                     {alg} (J)
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max energy values - filter out undefined
                const energyValues = algorithmKeys
                  .filter(key => results![key] !== undefined)
                  .map(key => results![key]!.energy);
                if (energyValues.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No results available yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                const minEnergy = Math.min(...energyValues);
                const maxEnergy = Math.max(...energyValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const result = results![key];
                      if (!result) {
                        return (
                          <TableCell key={key} align="center">-</TableCell>
                        );
                      }
                      const energy = result.energy;
                      const isBest = energy === minEnergy; // Lower is better for energy
                      const isWorst = energy === maxEnergy && maxEnergy !== minEnergy;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {energy.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Execution Cost Tab */}
      <TabPanel value={value} index={4}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center" sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max cost values - filter out undefined
                const costValues = algorithmKeys
                  .filter(key => results![key] !== undefined)
                  .map(key => results![key]!.cost);
                if (costValues.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No results available yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                const minCost = Math.min(...costValues);
                const maxCost = Math.max(...costValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const result = results![key];
                      if (!result) {
                        return (
                          <TableCell key={key} align="center">-</TableCell>
                        );
                      }
                      const cost = result.cost;
                      const isBest = cost === minCost; // Lower is better for cost
                      const isWorst = cost === maxCost && maxCost !== minCost;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {cost.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Response Time Tab */}
      <TabPanel value={value} index={5}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center">
                     {alg} (ms)
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max response time values - filter out undefined
                const responseTimeValues = algorithmKeys
                  .filter(key => results![key] !== undefined)
                  .map(key => results![key]!.responseTime);
                if (responseTimeValues.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No results available yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                const minResponseTime = Math.min(...responseTimeValues);
                const maxResponseTime = Math.max(...responseTimeValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const result = results![key];
                      if (!result) {
                        return (
                          <TableCell key={key} align="center">-</TableCell>
                        );
                      }
                      const responseTime = result.responseTime;
                      const isBest = responseTime === minResponseTime; // Lower is better for response time
                      const isWorst = responseTime === maxResponseTime && maxResponseTime !== minResponseTime;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {responseTime.toFixed(2)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Analysis Tab */}
      <TabPanel value={value} index={6}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            backgroundColor: '#fafafa',
            minHeight: 400,
            overflow: 'auto',
          }}
        >
          {!hasResults || !analysisData ? (
            <Typography variant="body2" color="text.secondary">
              No results available. Please run optimization.
            </Typography>
          ) : (
            <Box>
              {/* Header */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 2,
                  }}
                >
                  Analysis Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configuration: Tasks={results.configuration.taskCount}, Properties={results.configuration.propertyCount}, Population={results.configuration.initialPopulation}
                </Typography>
              </Box>

              {/* Fitness Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #1976d2',
                  }}
                >
                  1. FITNESS COMPARISON (Higher is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.fitnessData.length > 0 
                    ? `${analysisData.fitnessData[0].name} - ${analysisData.fitnessData[0].fitness.toFixed(6)}`
                    : 'No results available yet'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.fitnessData.length > 0 ? analysisData.fitnessData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.fitnessData.length - 1 && analysisData.fitnessData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.fitness.toFixed(6)}
                        </Typography>
                      </Box>
                    );
                  }) : (
                    <Typography variant="body2" color="text.secondary">
                      No results available yet.
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Energy Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#ed6c02',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #ed6c02',
                  }}
                >
                  2. ENERGY CONSUMPTION COMPARISON (Lower is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.energyData[0].name} - {analysisData.energyData[0].energy.toFixed(6)} Joules
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.energyData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.energyData.length - 1 && analysisData.energyData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.energy.toFixed(6)} Joules
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Response Time Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#9c27b0',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #9c27b0',
                  }}
                >
                  3. RESPONSE TIME COMPARISON (Lower is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.responseTimeData[0].name} - {analysisData.responseTimeData[0].responseTime.toFixed(2)} ms
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.responseTimeData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.responseTimeData.length - 1 && analysisData.responseTimeData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.responseTime.toFixed(2)} ms
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Combined Winner Comparison */}
              {(() => {
                const combinedWinner = calculateCombinedWinner(results);
                if (!combinedWinner) return null;

                return (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#2e7d32',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid #2e7d32',
                      }}
                    >
                      4. COMBINED WINNER (Best Solution + Lowest Energy)
                    </Typography>
                    {combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                          Perfect Winner(s) - #1 in BOTH Fitness AND Energy:
                        </Typography>
                        {combinedWinner.perfectWinners.map((winner) => (
                          <Box
                            key={winner.key}
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              animation: `${flashBest} 1.5s ease-in-out infinite`,
                              backgroundColor: 'rgba(76, 175, 80, 0.2)',
                              border: '2px solid',
                              borderColor: 'success.main',
                              mb: 1,
                              mr: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'success.main',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                              }}
                            >
                              {winner.name} - Fitness: {winner.fitness.toFixed(6)} (#1), Energy: {winner.energy.toFixed(6)} J (#1)
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : combinedWinner.bestAlternative ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                          Best Alternative (No algorithm wins both metrics):
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 193, 7, 0.2)',
                            border: '2px solid',
                            borderColor: 'warning.main',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: 'warning.main',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                            }}
                          >
                            {combinedWinner.bestAlternative.name} - Fitness: {combinedWinner.bestAlternative.fitness.toFixed(6)} 
                            ({combinedWinner.bestAlternative.fitness === combinedWinner.bestFitness ? '#1 - Best Solution' : `Rank #${combinedWinner.bestAlternative.fitnessRank}`}), 
                            Energy: {combinedWinner.bestAlternative.energy.toFixed(6)} J 
                            ({combinedWinner.bestAlternative.energy === combinedWinner.bestEnergy ? '#1 - Lowest Energy' : `Rank #${combinedWinner.bestAlternative.energyRank}`})
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    <Box>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        Combined Ranking:
                      </Typography>
                      {combinedWinner.rankedAlgorithms.map((alg, idx) => {
                        const isPerfect = alg.isPerfectWinner;
                        const isBestAlt = alg.isBestAlternative;
                        return (
                          <Box
                            key={alg.key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mb: 1,
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: isPerfect
                                ? 'rgba(76, 175, 80, 0.1)'
                                : isBestAlt
                                ? 'rgba(255, 193, 7, 0.1)'
                                : 'transparent',
                              animation: isPerfect
                                ? `${flashBest} 1.5s ease-in-out infinite`
                                : 'none',
                              border: isPerfect || isBestAlt ? '2px solid' : 'none',
                              borderColor: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'transparent',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                minWidth: 40,
                              }}
                            >
                              {idx + 1}.
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: isPerfect || isBestAlt ? 700 : 500,
                                color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                fontFamily: 'monospace',
                                minWidth: 100,
                              }}
                            >
                              {alg.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: isPerfect || isBestAlt ? 600 : 400,
                                color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                fontFamily: 'monospace',
                              }}
                            >
                              Fitness Rank #{alg.fitnessRank}, Energy Rank #{alg.energyRank}
                            </Typography>
                            {(isPerfect || isBestAlt) && (
                              <Chip
                                label={isPerfect ? 'Perfect Winner' : 'Best Alternative'}
                                size="small"
                                color={isPerfect ? 'success' : 'warning'}
                                sx={{ ml: 'auto' }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* Combined Winner Tab */}
      <TabPanel value={value} index={7}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            backgroundColor: '#fafafa',
            minHeight: 400,
            overflow: 'auto',
          }}
        >
          {!hasResults ? (
            <Typography variant="body2" color="text.secondary">
              No results available. Please run optimization.
            </Typography>
          ) : (() => {
            const combinedWinner = calculateCombinedWinner(results);
            if (!combinedWinner) {
              return (
                <Typography variant="body2" color="text.secondary">
                  No combined winner data available.
                </Typography>
              );
            }

            return (
              <Box>
                {/* Header */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: 2,
                    }}
                  >
                    Combined Winner
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Best Solution (Fitness) + Lowest Energy
                  </Typography>
                </Box>

                {/* Perfect Winner or Best Alternative */}
                {combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0 ? (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#2e7d32',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid #2e7d32',
                      }}
                    >
                      Perfect Winner(s) - #1 in BOTH Fitness AND Energy
                    </Typography>
                    {combinedWinner.perfectWinners.map((winner) => (
                      <Box
                        key={winner.key}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          animation: `${flashBest} 1.5s ease-in-out infinite`,
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          border: '2px solid',
                          borderColor: 'success.main',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <EmojiEventsIcon sx={{ color: 'success.main', mr: 1 }} />
                          <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                            {winner.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 4 }}>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'success.dark' }}>
                            Fitness: {winner.fitness.toFixed(6)} (#1 - Best Solution)
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'success.dark' }}>
                            Energy: {winner.energy.toFixed(6)} J (#1 - Lowest Energy)
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : combinedWinner.bestAlternative ? (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#ed6c02',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid #ed6c02',
                      }}
                    >
                      Best Alternative (No algorithm wins both metrics)
                    </Typography>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 193, 7, 0.2)',
                        border: '2px solid',
                        borderColor: 'warning.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmojiEventsIcon sx={{ color: 'warning.main', mr: 1 }} />
                        <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                          {combinedWinner.bestAlternative.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 4 }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'warning.dark' }}>
                          Fitness: {combinedWinner.bestAlternative.fitness.toFixed(6)} 
                          ({combinedWinner.bestAlternative.fitness === combinedWinner.bestFitness 
                            ? '#1 - Best Solution' 
                            : `Rank #${combinedWinner.bestAlternative.fitnessRank}`})
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'warning.dark' }}>
                          Energy: {combinedWinner.bestAlternative.energy.toFixed(6)} J 
                          ({combinedWinner.bestAlternative.energy === combinedWinner.bestEnergy 
                            ? '#1 - Lowest Energy' 
                            : `Rank #${combinedWinner.bestAlternative.energyRank}`})
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, pl: 4 }}>
                        No algorithm wins both metrics. This is the closest match.
                      </Typography>
                    </Box>
                  </Box>
                ) : null}

                {/* Combined Ranking Table */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1976d2',
                      mb: 2,
                      pb: 1,
                      borderBottom: '2px solid #1976d2',
                    }}
                  >
                    Combined Ranking
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Algorithm</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Fitness</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Fitness Rank</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Energy (J)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Energy Rank</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combinedWinner.rankedAlgorithms.map((alg, idx) => {
                          const isPerfect = alg.isPerfectWinner;
                          const isBestAlt = alg.isBestAlternative;
                          return (
                            <TableRow
                              key={alg.key}
                              sx={{
                                backgroundColor: isPerfect
                                  ? 'rgba(76, 175, 80, 0.1)'
                                  : isBestAlt
                                  ? 'rgba(255, 193, 7, 0.1)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isPerfect
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : isBestAlt
                                    ? 'rgba(255, 193, 7, 0.2)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isPerfect || isBestAlt ? 700 : 600,
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  {idx + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isPerfect || isBestAlt ? 700 : 500,
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  {alg.name}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  {alg.fitness.toFixed(6)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isPerfect || isBestAlt ? 600 : 400,
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  #{alg.fitnessRank}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  {alg.energy.toFixed(6)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isPerfect || isBestAlt ? 600 : 400,
                                    color: isPerfect ? 'success.main' : isBestAlt ? 'warning.main' : 'text.primary',
                                  }}
                                >
                                  #{alg.energyRank}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {isPerfect ? (
                                  <Chip label="Perfect Winner" size="small" color="success" />
                                ) : isBestAlt ? (
                                  <Chip label="Best Alternative" size="small" color="warning" />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            );
          })()}
        </Paper>
      </TabPanel>

      {/* Statistics Tab */}
      {results && 'statistics' in results && results.statistics && (
        <TabPanel value={value} index={8}>
          <StatisticsView results={results} />
        </TabPanel>
      )}
    </Box>
  );
};

export default ResultsView;
